// frontend/src/hooks/useScreenSize.js
import { useScreen } from '../contexts/ScreenContext';

/**
 * Custom Hook für responsive Screen-Eigenschaften
 * Vereinfacht den Zugriff auf Screen-Informationen in Komponenten
 */
export const useScreenSize = () => {
  const screen = useScreen();

  return {
    // Screen Dimensions
    width: screen.width,
    height: screen.height,
    
    // Orientation & Aspect Ratio
    orientation: screen.orientation,
    aspectRatio: screen.aspectRatio,
    isPortrait: screen.helpers.isPortrait(),
    isLandscape: screen.helpers.isLandscape(),
    
    // Resolution Category
    category: screen.category,
    breakpoint: screen.breakpoint,
    
    // Pixel Ratio (for retina displays)
    pixelRatio: screen.pixelRatio,
    isRetina: screen.pixelRatio > 1,
    
    // Responsive Multipliers
    fontSizeMultiplier: screen.helpers.getFontSizeMultiplier(),
    spacingMultiplier: screen.helpers.getSpacingMultiplier(),
    
    // Helper Functions
    isMinCategory: screen.helpers.isMinCategory,
    getResponsiveClass: screen.helpers.getResponsiveClass,
    
    // Specific Size Checks
    isSmall: screen.category === 'small',
    isMedium: screen.category === 'medium',
    isLarge: screen.category === 'large',
    isXLarge: screen.category === 'xlarge',
    
    // HD/2K/4K checks
    isHD: screen.breakpoint === 'HD',
    is2K: screen.breakpoint === '2K',
    is4K: screen.breakpoint === '4K',
    
    // Refresh function
    refresh: screen.refresh
  };
};

/**
 * Hook für responsive Font Size (in rem)
 * @param {number} baseSize - Base font size in rem
 * @returns {string} Responsive font size with unit
 */
export const useResponsiveFontSize = (baseSize = 1) => {
  const { fontSizeMultiplier } = useScreenSize();
  return `${baseSize * fontSizeMultiplier}rem`;
};

/**
 * Hook für responsive Spacing (in rem)
 * @param {number} baseSpacing - Base spacing in rem
 * @returns {string} Responsive spacing with unit
 */
export const useResponsiveSpacing = (baseSpacing = 1) => {
  const { spacingMultiplier } = useScreenSize();
  return `${baseSpacing * spacingMultiplier}rem`;
};

export default useScreenSize;